// The docked toolbar's rows. Two arrangements, kept apart: the user's OWN rows
// (what they made by dragging — saved) and those rows wrapped to the dock's width
// (derived on every render — never saved). A drag edits the displayed rows and is
// written back as the user's rows with the automatic wraps joined up again, so a
// wider window takes the rows back.

/** A displayed row. `soft`: it continues the row above — an automatic wrap, not
 *  a break the user made. */
export interface DockRow { ids: string[]; soft: boolean }

/** Wrap each of the user's rows to `avail` px (`firstAvail` for the first
 *  displayed row, which shares its line with the brand). A row is only ever
 *  split, never merged with the next. */
export function wrapRows(
  rows: string[][], width: (id: string) => number, avail: number, firstAvail: number, gap: number,
): DockRow[] {
  const out: DockRow[] = [];
  for (const row of rows) {
    let cur: string[] = [];
    let curW = 0;
    let soft = false;
    for (const id of row) {
      const w = width(id);
      const rowAvail = out.length === 0 ? firstAvail : avail;
      if (cur.length && curW + gap + w > rowAvail) {
        out.push({ ids: cur, soft });
        cur = []; curW = 0; soft = true;
      }
      curW += (cur.length ? gap : 0) + w;
      cur.push(id);
    }
    if (cur.length) out.push({ ids: cur, soft });
  }
  return out;
}

/** Back to the user's rows: every soft row joins the one above it. */
export function joinRows(rows: DockRow[]): string[][] {
  const out: string[][] = [];
  for (const r of rows) {
    if (!r.ids.length) continue;
    if (r.soft && out.length) out[out.length - 1].push(...r.ids);
    else out.push([...r.ids]);
  }
  return out;
}

/** Where a dragged section lands on the DISPLAYED rows: into row `at` before
 *  `beforeId` (null = its end), or, with `newRow`, on a new row inserted before
 *  displayed row `at` (past the last = at the bottom). */
export interface DockDrop { newRow: boolean; at: number; beforeId: string | null }

/** Move section `id` as dropped; returns the user's new rows. A new row is a
 *  break the user made, so the row after it starts a row of its own too. */
export function dropSection(shown: DockRow[], id: string, drop: DockDrop): string[][] {
  const rows = shown.map((r) => ({ ids: [...r.ids], soft: r.soft }));
  const target = drop.newRow ? null : rows[Math.min(drop.at, rows.length - 1)] ?? null;
  const after = drop.newRow ? rows[drop.at] ?? null : null;
  for (const r of rows) r.ids = r.ids.filter((x) => x !== id);
  if (drop.newRow) {
    if (after) after.soft = false;
    const at = after ? rows.indexOf(after) : rows.length;
    rows.splice(at, 0, { ids: [id], soft: false });
  } else if (target) {
    const i = drop.beforeId ? target.ids.indexOf(drop.beforeId) : -1;
    target.ids.splice(i < 0 ? target.ids.length : i, 0, id);
  } else {
    rows.push({ ids: [id], soft: false });
  }
  // A row emptied by the drag hands its start-of-row to the row it was wrapping.
  for (let i = 0; i < rows.length; i++) {
    if (rows[i].ids.length) continue;
    if (!rows[i].soft && rows[i + 1]) rows[i + 1].soft = false;
    rows.splice(i--, 1);
  }
  return joinRows(rows);
}
